import { client } from "../api/client.js";
import { toast } from "./toast.js";

// Inline 12px chevron icons — left = expanded, right = collapsed.
// Replaces the previous «/» ASCII characters which rendered inconsistently
// across font stacks and didn't scale cleanly.
const _CHEVRON_LEFT  = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>';
const _CHEVRON_RIGHT = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>';

export function initNavBindings(router) {
  const sidebar = document.getElementById("app-sidebar");
  const toggleBtn = document.getElementById("sidebar-toggle");
  const navItems = document.querySelectorAll("[data-page]");

  // Sidebar Collapse Logic
  const setSidebarState = (isCollapsed) => {
    if (isCollapsed) {
      sidebar.classList.add("collapsed");
      toggleBtn.innerHTML = _CHEVRON_RIGHT;
      toggleBtn.setAttribute("aria-label", "Expand sidebar");
      navItems.forEach(item => {
        const label = item.querySelector(".nav-label")?.textContent;
        if (label) item.setAttribute("title", label);
      });
    } else {
      sidebar.classList.remove("collapsed");
      toggleBtn.innerHTML = _CHEVRON_LEFT;
      toggleBtn.setAttribute("aria-label", "Collapse sidebar");
      navItems.forEach(item => item.removeAttribute("title"));
    }
  };

  // Init from localStorage
  const savedState = localStorage.getItem("app.sidebarCollapsed");
  if (savedState === "1") {
    setSidebarState(true);
  }

  toggleBtn?.addEventListener("click", () => {
    const isNowCollapsed = !sidebar.classList.contains("collapsed");
    setSidebarState(isNowCollapsed);
    localStorage.setItem("app.sidebarCollapsed", isNowCollapsed ? "1" : "0");
  });

  // Mobile hamburger menu
  const mobileMenuBtn = document.getElementById("mobile-menu-btn");
  const sidebarOverlay = document.getElementById("sidebar-overlay");

  function openMobileSidebar() {
    sidebar.classList.add("mobile-open");
    sidebarOverlay?.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  function closeMobileSidebar() {
    sidebar.classList.remove("mobile-open");
    sidebarOverlay?.classList.remove("active");
    document.body.style.overflow = "";
  }

  mobileMenuBtn?.addEventListener("click", () => {
    if (sidebar.classList.contains("mobile-open")) {
      closeMobileSidebar();
    } else {
      openMobileSidebar();
    }
  });

  sidebarOverlay?.addEventListener("click", closeMobileSidebar);

  // Close sidebar on Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && sidebar.classList.contains("mobile-open")) {
      closeMobileSidebar();
    }
  });

  // Handle Sidebar Links. Items with data-hash (e.g. "#memory?tab=chat")
  // take precedence over the bare data-page hash, so a sidebar entry can
  // deep-link into a sub-tab of an existing route.
  navItems.forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const dataHash = el.getAttribute("data-hash");
      const page = el.getAttribute("data-page");
      location.hash = dataHash || ("#" + page);
      closeMobileSidebar();
    });
  });

  // Handle Logout
  const logoutBtn = document.querySelector('[data-testid="logout"]');
  if (logoutBtn) {
    logoutBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      try {
        await client.authLogout();
        window.location.hash = "#login";
        window.location.reload();
      } catch (err) {
        console.error("Logout failed:", err);
        toast('error', 'Sign out failed — please try again.');
      }
    });
  }

  // Update Active State. When two nav items target the same data-page
  // (e.g. Chat → #memory?tab=chat and The Brain → #memory), prefer the
  // entry whose data-hash params are a subset of the current hash. The
  // permissive subset match means "#memory?tab=chat" stays active even
  // if the URL has additional one-shot params like "&new=1".
  function hashMatches(itemHash, currentHash) {
    if (itemHash === currentHash) return true;
    const [iBase, iQs] = itemHash.split("?");
    const [cBase, cQs] = currentHash.split("?");
    if (iBase !== cBase) return false;
    if (!iQs) return false; // item declares no query — only an exact match counts
    const iParams = new URLSearchParams(iQs);
    const cParams = new URLSearchParams(cQs || "");
    for (const [k, v] of iParams) {
      if (cParams.get(k) !== v) return false;
    }
    return true;
  }
  function updateActive() {
    const currentHash = location.hash || "#home";
    const baseRoute = currentHash.split("?")[0];
    // Pass 1: collect data-hash winners per page. Longer data-hash wins
    // when multiple match (most specific).
    const dataHashWinnerByPage = new Map();
    navItems.forEach(el => {
      const dataHash = el.getAttribute("data-hash");
      if (!dataHash) return;
      if (!hashMatches(dataHash, currentHash)) return;
      const page = el.getAttribute("data-page");
      const existing = dataHashWinnerByPage.get(page);
      if (!existing || (existing.getAttribute("data-hash") || "").length < dataHash.length) {
        dataHashWinnerByPage.set(page, el);
      }
    });
    // Pass 2: bare items win only when no data-hash variant claimed
    // their page.
    const winners = new Set(dataHashWinnerByPage.values());
    navItems.forEach(el => {
      if (winners.has(el)) return;
      if (el.getAttribute("data-hash")) return;
      const page = el.getAttribute("data-page");
      if (dataHashWinnerByPage.has(page)) return;
      if (baseRoute === "#" + page) winners.add(el);
    });
    navItems.forEach(el => el.classList.toggle("active", winners.has(el)));
  }
  window.addEventListener("hashchange", updateActive);
  // Run once on boot so the active state reflects the initial hash.
  updateActive();

  // ── Collapsible nav group toggles (e.g. INTELLIGENCE on first session) ──
  // Each toggle persists its expanded state per group key. If the route
  // currently belongs to a collapsed group, we auto-expand once so the
  // active item is visible — the user shouldn't see a hidden "active"
  // entry. The permanent flip persists so subsequent sessions stay open.
  const groupToggles = document.querySelectorAll('.nav-group-toggle[data-group]');
  function syncGroupExpanded(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    try {
      localStorage.setItem('app.navGroup.' + toggle.dataset.group, expanded ? '1' : '0');
    } catch (_) {}
  }
  groupToggles.forEach((toggle) => {
    const key = 'app.navGroup.' + toggle.dataset.group;
    let stored = null;
    try { stored = localStorage.getItem(key); } catch (_) {}
    // Default = collapsed (cuts first-paint density). Once a user has
    // expanded once, the value persists as '1' forever.
    syncGroupExpanded(toggle, stored === '1');
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      syncGroupExpanded(toggle, !isOpen);
    });
  });
  function autoExpandActiveGroup() {
    groupToggles.forEach((toggle) => {
      const items = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!items) return;
      // If the active item lives inside this group, force-expand and
      // persist. The user has effectively "discovered" the group, so
      // hiding it again would be hostile.
      const hasActive = items.querySelector('.nav-item.active');
      if (hasActive && toggle.getAttribute('aria-expanded') !== 'true') {
        syncGroupExpanded(toggle, true);
      }
    });
  }
  autoExpandActiveGroup();
  window.addEventListener('hashchange', autoExpandActiveGroup);

  window.__router__ = router;
}
